import { VariantColor } from '@repo/toolkit/colors'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { Column } from '@repo/ui/data-table'
import { DeleteIcon, EmailIcon, MessageIcon, PlusIcon, PrintIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { isAfter } from 'date-fns'
import { toast } from 'sonner'

import { API, constructApiUrl } from '../../../api/apis.config'
import { radSendEmailToStaffs } from '../../../api/rads/send-email-to-staffs'
import { radSendReminderToStaff } from '../../../api/rads/send-reminder-to-staff'
import { updateRadAffectedStaff } from '../../../api/rads/update-rad-affected-staff'
import { Paths } from '../../../lib/constants/path'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { staffPositionEnumMapping } from '../../../lib/types/enums/staff/staff-position.enum'
import { AffectedStaffFilters } from '../../../lib/types/models/affectedStaff/affected-staff-input'
import { RadParticipant } from '../../../lib/types/models/rad/rad-participant'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { constructCenterPath } from '../../../views/centers/utils'
import CountryFlag from '../../elements/country-flag'
import DynamicDataTable, { DynamicTableRef } from '../../elements/dynamic-table'
import { QualificationBadge } from '../../elements/qualification-badge'
import SingleComment from '../../elements/single-comment'
import TransportPicto from '../../elements/transport-picto'
import SendEmailForm from '../candidates/send-email-form'
import PrintPliButton from './print-pli-button'
import UpdateRadCommentForm from './update-rad-comment-form'
import UpdateRadSwitch from './update-rad-switch'

interface RadStaffTableProps {
  radId: number
  tableRef: React.RefObject<DynamicTableRef<RadParticipant>>
}

const dateBadgeVariants: VariantColor[] = ['fushia', 'success', 'secondaryRed', 'yellow', 'blue']

const RadStaffTable = ({ radId, tableRef }: RadStaffTableProps) => {
  const { t } = useTranslate()
  const { modalState, selectedData, toggleModal } = useModalManager<
    'send-email',
    RadParticipant[]
  >(['send-email'])

  const {
    modalState: commentModal,
    selectedData: comment,
    toggleModal: toggleComment,
    closeModel: closeComment
  } = useModalManager<'add-rad-comment' | 'delete-rad-comment', RadParticipant>([
    'add-rad-comment',
    'delete-rad-comment'
  ])

  const { navigate } = useNavigator()
  const { downloadDocuments } = useDownloadDocuments()

  const { mutateAsync: sendEmailToStaffsMutation } = useMutation({
    mutationFn: radSendEmailToStaffs,
    onSuccess: () => {
      toast.success('Email envoyé avec succès')
      toggleModal('send-email')
    },
    onError: showAxiosError
  })

  const { mutateAsync: sendReminderToStaff } = useMutation({
    mutationFn: radSendReminderToStaff,
    onSuccess: () => toast.success('Email de relance envoyé avec succès'),
    onError: showAxiosError
  })

  const { mutateAsync: deleteComment } = useMutation({
    mutationFn: updateRadAffectedStaff,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Commentaire supprimé avec succès')
      tableRef.current?.refetch()
      closeComment('delete-rad-comment')
    }
  })

  const selectedEmails = [
    ...new Set(selectedData?.map((row) => row.affectedStaff.staff.user.email))
  ]

  const columns: Column<RadParticipant>[] = [
    {
      accessorKey: 'affectedStaff.staff.firstName',
      header: "Nom de l'encadrant",
      enableSorting: true,
      cell: ({ row }) => (
        <Typography.Link
          variant="secondary-body"
          onClick={() =>
            navigate(
              Paths.AFFECTED_STAFFS_VIEW_PAGE.replace(
                ':id',
                row.original.affectedStaff.id.toString()
              )
            )
          }
        >
          {toFullName(
            row.original.affectedStaff.staff.firstName,
            row.original.affectedStaff.staff.lastName
          )}
        </Typography.Link>
      )
    },
    {
      accessorKey: 'affectedStaff.staff.missionsCount',
      header: 'Ancienneté',
      cell: ({ row }) => (
        <Typography variant="secondary-body">
          {row.original.affectedStaff.totalJourneys ?? 0}{' '}
          {row.original.affectedStaff.totalJourneys === 1 ? 'séjour' : 'séjours'}
        </Typography>
      )
    },
    {
      accessorKey: 'affectedStaff.staff.postalCode',
      header: 'Département',
      cell: ({ row }) => row.original.affectedStaff.staff.postalCode?.slice(0, 2) ?? '-'
    },
    {
      accessorKey: 'affectedStaff.staff.position',
      header: 'Poste',
      enableSorting: true,
      cell: ({ row }) => (
        <Typography variant="secondary-body">
          {t(staffPositionEnumMapping[row.original.affectedStaff.staff.position])}
        </Typography>
      )
    },
    {
      accessorKey: 'affectedStaff.staffRequest.qualification',
      header: 'Qualification',
      enableSorting: true,
      cell: ({ row }) => (
        <QualificationBadge
          qualification={row.original.affectedStaff.staffRequest.qualification}
        />
      )
    },
    {
      accessorKey: 'affectedStaff.staff.country',
      header: 'Pays',
      cell: ({ row }) => <CountryFlag possibleCountry={row.original.affectedStaff.staff.country} />
    },
    {
      accessorKey: 'affectedStaff.viewData.lastJourneyDepartureDate',
      header: 'Date du séjour',
      enableSorting: true,
      cell: ({ row }) => (
        <Badge
          variant={
            dateBadgeVariants[
              row.original.affectedStaff.staffRequest.centers.length % dateBadgeVariants.length
            ]
          }
          text={(() => {
            const dates = row.original.affectedStaff.staffRequest.centers
              .map((center) =>
                center.centerToJourneys.map((centerToJourney) => centerToJourney.journey.date)
              )
              .flat()
            // Latest departure, matching the DB sort key (AffectedStaffView.lastJourneyDepartureDate)
            const lastDate = dates.reduce<(typeof dates)[number] | undefined>(
              (latest, date) =>
                !latest || isAfter(date.departureDate, latest.departureDate) ? date : latest,
              undefined
            )
            if (!lastDate) return '-'
            return `${DateFormatter.DDMMYYYY(lastDate.departureDate)} - ${DateFormatter.DDMMYYYY(lastDate.returnDate)}`
          })()}
        />
      )
    },
    {
      accessorKey: 'affectedStaff.viewData.formulasLabel',
      header: 'Formule',
      enableSorting: true,
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          {row.original.affectedStaff.staffRequest.formulas.map((formula) => (
            <Typography variant="secondary-body" key={formula.id}>
              {getTranslatedName(formula)}
            </Typography>
          ))}
        </div>
      )
    },
    {
      accessorKey: 'affectedStaff.viewData.centersLabel',
      header: 'Centre',
      enableSorting: true,
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          {row.original.affectedStaff.staffRequest.centers.map((center) => (
            <Typography.Link
              onClick={() =>
                navigate(constructCenterPath(Paths.CENTER_GLOBAL_VIEW_PAGE, center.id))
              }
              variant="secondary-body"
              key={center.id}
            >
              {center.name}
            </Typography.Link>
          ))}
        </div>
      )
    },
    {
      accessorKey: 'affectedStaff.trip',
      header: 'Voyage',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.affectedStaff?.trip?.transportMeans?.picto ? (
          <TransportPicto picto={row.original.affectedStaff.trip.transportMeans.picto} />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'partcipatedInRad',
      header: 'Participe à la RAD?',
      enableSorting: true,
      columnHeaderClassName: 'text-center',
      cell: ({ row }) => (
        <div className="w-28 flex justify-center">
          <UpdateRadSwitch
            participantId={row.original.id}
            radId={radId}
            name={'partcipatedInRad'}
            checked={row.original.partcipatedInRad}
            onSuccess={() => tableRef.current?.refetch()}
          />
        </div>
      )
    },
    {
      accessorKey: 'wasAbsentToRad',
      header: 'Etait absent(e)?',
      enableSorting: true,
      columnHeaderClassName: 'text-center',
      cell: ({ row }) => (
        <div className="w-20 flex justify-center">
          <UpdateRadSwitch
            radId={radId}
            participantId={row.original.id}
            name={'wasAbsentToRad'}
            checked={row.original.wasAbsentToRad}
            onSuccess={() => tableRef.current?.refetch()}
          />
        </div>
      )
    },
    {
      accessorKey: 'pli',
      header: 'Pli',
      cell: ({ row }) =>
        row.original.canPrintPli ? (
          <PrintPliButton
            pli={{
              path: row.original.pli?.path,
              pliModifiedAt: row.original.pliModifiedAt,
              pliPrintedAt: row.original.pliPrintedAt
            }}
            type="participant"
            participantId={row.original.id}
            radId={radId}
            onSuccess={() => tableRef.current?.refetch()}
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'radComment',
      header: 'Mémo/commentaire',
      cell: ({ row }) =>
        row.original.radComment ? (
          <div className="mr-2">
            <SingleComment
              comment={row.original.radComment}
              onDelete={() => toggleComment('delete-rad-comment', row.original)}
            />
          </div>
        ) : (
          <Button
            icon={PlusIcon}
            label="Ajouter commentaire"
            onClick={() => toggleComment('add-rad-comment', row.original)}
          />
        )
    }
  ]

  return (
    <div>
      <DynamicDataTable<RadParticipant, AffectedStaffFilters>
        ref={tableRef}
        columns={columns}
        headerType="hide-total"
        title="Encadrants"
        selectable
        endpoint={API.rads.getParticipants.url.replace(':id', radId.toString())}
        permissions={{ delete: false }}
        actions={[
          {
            label: 'Envoyer un mail groupé',
            icon: EmailIcon,
            fn: (rows) => toggleModal('send-email', rows),
            variant: 'ghost',
            type: 'multiple'
          },
          {
            label: 'Envoyer un mail de relance manuelle',
            icon: EmailIcon,
            refetch: true,
            fn: (rows) => {
              const staffIds = rows.map((row) => row.affectedStaff.staff.id)
              sendReminderToStaff({ staffIds, id: radId })
            },
            variant: 'ghost',
            type: 'multiple'
          },
          {
            label: 'Imprimer les affiches par centre',
            icon: PrintIcon,
            fn: async (rows) => {
              await downloadDocuments({
                ids: rows.map((r) => r.id),
                endpoint: constructApiUrl(API.rads.printNameCards.url, { id: radId })
              })
            },
            variant: 'ghost',
            type: 'multiple'
          },
          {
            label: 'Imprimer les affiches par ordre alphabétique',
            icon: PrintIcon,
            fn: async (rows) => {
              await downloadDocuments({
                ids: rows.map((r) => r.id),
                endpoint: constructApiUrl(API.rads.printAttendanceSheetAlphabetically.url, {
                  id: radId
                })
              })
            },
            variant: 'ghost',
            type: 'multiple'
          },
          {
            label: "Imprimer la feuille d'émargement",
            icon: PrintIcon,
            fn: async (rows) => {
              await downloadDocuments({
                ids: rows.map((r) => r.id),
                endpoint: constructApiUrl(API.rads.printAttendanceSheetRandomly.url, {
                  id: radId
                })
              })
            },
            variant: 'ghost',
            type: 'multiple'
          }
        ]}
      />

      {/* Send email */}
      {selectedData && (
        <Modal
          isOpen={modalState['send-email']}
          title={'Envoyer un email'}
          icon={EmailIcon}
          size="medium"
          hideActions
          onCancel={() => {
            toggleModal('send-email')
          }}
        >
          <SendEmailForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={{
              emails: selectedEmails,
              subject: '',
              body: ''
            }}
            labels={[
              ...new Map(
                selectedData.map((p) => [
                  p.affectedStaff.staff.user.email,
                  `${toFullName(p.affectedStaff.staff.firstName, p.affectedStaff.staff.lastName)} - ${p.affectedStaff.staff.user.email}`
                ])
              ).values()
            ]}
            buttonsConfig={modalButtonConfig({
              onCancel: () => toggleModal('send-email'),
              confirmText: 'Envoyer',
              onConfirm: (values) =>
                sendEmailToStaffsMutation({ ...values, emails: selectedEmails })
            })}
          />
        </Modal>
      )}
      <Modal
        isOpen={commentModal['add-rad-comment']}
        title={'Ajouter commentaire'}
        size="small"
        hideActions
        icon={MessageIcon}
        onCancel={() => closeComment('add-rad-comment')}
      >
        {comment && (
          <UpdateRadCommentForm
            radId={radId}
            participantId={comment.id}
            className={MODAL_BODY_CLASSNAME}
            onSuccess={() => {
              closeComment('add-rad-comment')
              tableRef.current?.refetch()
            }}
            onCancel={() => closeComment('add-rad-comment')}
          />
        )}
      </Modal>

      <Modal
        isOpen={commentModal['delete-rad-comment']}
        title={'Êtes-vous sûr de vouloir supprimer ce commentaire ?'}
        size="small"
        variant="destructive"
        icon={DeleteIcon}
        actionButtonsOptions={{
          confirmText: 'Suprimer',
          onConfirm: () => {
            if (comment) deleteComment({ id: radId, participantId: comment.id, radComment: null })
          },
          cancelText: 'Annuler'
        }}
        onCancel={() => closeComment('delete-rad-comment')}
      />
    </div>
  )
}

export default RadStaffTable
